import React, { useState, useRef, useEffect, useCallback } from 'react'
import { pdfjs } from 'react-pdf'
import { Scrollbars } from 'react-custom-scrollbars-2'
import useIsMobile from '../../../screens/Contractor/PayablesTab/hooks/useIsMobile'

interface PdfViewerProps {
  pdfUrl: string
  onReachedAgreementEnd?: () => void
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  pdfUrl,
  onReachedAgreementEnd,
}) => {
  const [isAtTop, setIsAtTop] = useState(true)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [pdfDocument, setPdfDocument] = useState<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const scrollbarsRef = useRef(null)
  const activeRenders = useRef(new Set<number>())
  const [hasReachedEnd, setHasReachedEnd] = useState(false)
  const [pagesLoaded, setPagesLoaded] = useState(0)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  const isMobile = useIsMobile()

  // Handle screen resize to update the width
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    handleResize() // Set the initial width

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Load PDF Document
  useEffect(() => {
    const loadPdf = async () => {
      try {
        const loadingTask = pdfjs.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        setPdfDocument(pdf)
      } catch (error) {
        console.error('Error loading PDF: ', error)
      }
    }
    loadPdf()
  }, [pdfUrl])

  // Render PDF Page
  const renderPage = useCallback(
    async (pageNum: number) => {
      if (!pdfDocument || activeRenders.current.has(pageNum)) return
      activeRenders.current.add(pageNum)

      try {
        const page = await pdfDocument.getPage(pageNum)
        const viewport = page.getViewport({ scale: 1 })
        const containerWidth =
          containerRef.current?.clientWidth || viewport.width
        const scale = containerWidth / viewport.width
        const scaledViewport = page.getViewport({ scale })

        const canvas = document.getElementById(
          `page-${pageNum}`,
        ) as HTMLCanvasElement | null
        if (canvas) {
          canvas.width = scaledViewport.width
          canvas.height = scaledViewport.height

          const context = canvas.getContext('2d')
          if (context) {
            const renderContext = {
              canvasContext: context,
              viewport: scaledViewport,
            }
            await page.render(renderContext).promise
            setPagesLoaded((loaded) => loaded + 1)
          }
        }
      } catch (error) {
        console.error(`Error rendering page ${pageNum}: `, error)
      } finally {
        activeRenders.current.delete(pageNum)
      }
    },
    [pdfDocument],
  )

  // Render visible pages when scrolling
  const handleScroll = useCallback(() => {
    if (!pdfDocument || !containerRef.current) return

    const container = containerRef.current
    const scrollTop = container.scrollTop
    const containerHeight = container.clientHeight

    for (let i = 1; i <= pdfDocument.numPages; i++) {
      const canvas = document.getElementById(
        `page-${i}`,
      ) as HTMLCanvasElement | null
      const pageOffset = (i - 1) * (canvas ? canvas.height : 0)

      if (pageOffset >= scrollTop && pageOffset < scrollTop + containerHeight) {
        renderPage(i)
      }
    }
  }, [pdfDocument, renderPage])

  const handleScrollUpdate = useCallback(
    ({ scrollTop, scrollHeight, clientHeight }) => {
      const isAtScrollBottom = scrollTop + clientHeight >= scrollHeight - 1

      if (
        isAtScrollBottom &&
        !hasReachedEnd &&
        pagesLoaded === pdfDocument?.numPages
      ) {
        setHasReachedEnd(true)
        if (onReachedAgreementEnd) {
          onReachedAgreementEnd()
        }
      }

      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 30)
      setIsAtTop(pagesLoaded === pdfDocument?.numPages && scrollTop <= 30)
    },
    [hasReachedEnd, onReachedAgreementEnd, pagesLoaded, pdfDocument?.numPages],
  )

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  // Render the first page initially
  useEffect(() => {
    if (pdfDocument && pdfDocument.numPages > 0) {
      renderPage(1)
      handleScroll()
    }
  }, [pdfDocument, renderPage, handleScroll])

  const borderRadiusStyle = isAtTop
    ? {
        borderTopLeftRadius: 40,
        borderTopRightRadius: 40,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }
    : isAtBottom
    ? {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 40,
        borderBottomRightRadius: 40,
      }
    : { borderRadius: 0 }

  return (
    <Scrollbars
      ref={scrollbarsRef}
      style={{
        maxWidth: isMobile ? (screenWidth <= 415 ? 475 : 500) : 880,
        width: isMobile ? (screenWidth <= 415 ? 360 : 440) : 880,
        height: 600,
        border:
          pagesLoaded !== pdfDocument?.numPages ? 'none' : '2px solid #F8F9F9',
        ...borderRadiusStyle,
      }}
      autoHide
      autoHideTimeout={1000}
      autoHideDuration={200}
      onUpdate={handleScrollUpdate}
      renderThumbVertical={({ style, ...props }) => (
        <div
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          style={{
            ...style,
            backgroundColor: '#626D75',
            borderRadius: 10,
            width: 8,
          }}
        />
      )}
      renderTrackVertical={({ style, ...props }) => (
        <div
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          style={{
            ...style,
            right: 2,
            top: 30,
            bottom: 54,
            borderRadius: 10,
            width: 8,
          }}
        />
      )}
    >
      <div
        ref={containerRef}
        style={{
          maxWidth: isMobile ? (screenWidth <= 415 ? 475 : 500) : 840,
          width: isMobile ? (screenWidth <= 415 ? 420 : 400) : 840,
          borderRadius: 40,
          padding: 20,
          paddingBottom: 40,
          backgroundColor: '#F8F9F9',
        }}
      >
        {/* Render each page as a canvas */}
        {Array.from({ length: pdfDocument?.numPages || 0 }, (_, i) => (
          <div key={i + 1} style={{ position: 'relative', margin: '10px 0' }}>
            <canvas
              id={`page-${i + 1}`}
              style={{
                width: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            />
          </div>
        ))}
      </div>
    </Scrollbars>
  )
}

export default PdfViewer
